import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/lenis";
import { Logo } from "@/components/Logo";

const LINKS = [
  { label: "Products", id: "products" },
  { label: "Savings", id: "savings" },
  { label: "Designer", id: "designer" },
  { label: "Why Us", id: "why" },
  { label: "Process", id: "process" },
  { label: "Contact", id: "quote" },
];

const go = (id) => scrollToId(id);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "glass border-b border-gold/10 py-3" : "bg-transparent py-5"
        }`}
      >
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 flex items-center justify-between">
          <Logo onClick={() => go("top")} />
          <nav className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) => (
              <button
                key={l.id}
                type="button"
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="text-[13px] font-medium text-mutegray hover:text-warm transition-colors duration-300 tracking-wide"
              >
                {l.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="nav-cta-quote"
              onClick={() => go("quote")}
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-[13px] font-bold text-ink transition-all duration-300 hover:bg-goldlight hover:shadow-[0_0_28px_rgba(245,158,11,0.4)]"
            >
              Get Free Quote
              <ArrowUpRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              data-testid="nav-mobile-toggle"
              aria-label="Open menu"
              onClick={() => setOpen(true)}
              className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-warm"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-xl flex flex-col"
          >
            <div className="flex items-center justify-between px-5 sm:px-8 py-5">
              <Logo onClick={() => { setOpen(false); go("top"); }} />
              <button
                type="button"
                data-testid="nav-mobile-close"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-warm"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  type="button"
                  data-testid={`nav-mobile-link-${l.id}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  onClick={() => { setOpen(false); go(l.id); }}
                  className="text-left font-display text-4xl font-bold text-warm/90 hover:text-gold transition-colors py-2"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                type="button"
                data-testid="nav-mobile-cta"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.44, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => { setOpen(false); go("quote"); }}
                className="mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-ink"
              >
                Get Free Quote <ArrowUpRight className="h-4 w-4" />
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
